import React from 'react';

import CellPicker from 'src/components/base/CellPicker';
import CheckboxWithTooltip from 'src/components/base/CheckboxWithTooltip';
import Flowstep from 'src/components/Flowsteps/base/Flowstep';
import SearchableComboBox from 'src/components/base/SearchableComboBox';


import office from 'src/services/office';
import { Type, PullMode } from 'shared/utils/flowstep';
import { Step } from 'src/components/Flowsteps/base/Step'
import { NEW_WORKSHEET_ID } from '@v2/components/base/CellAddressPicker/hooks/useCellAddressValidation';


const Const = {
  PullModeOptions: [
    { key: PullMode.Overwrite, text: 'Overwrite', tooltip: 'On refresh, overwrites the data in the range where it was initially pulled.' },
    { key: PullMode.Append, text: 'Append', tooltip: 'On refresh, keeps originally pulled data in place and appends the all pull results at the end of the existing data. May cause duplicate rows. Make sure you pull a Date or DateTime column to be able to distinguish between the older and newer results.' },
    { key: PullMode.NewSheet, text: 'New sheet', tooltip: 'Every refresh will create a separate timestamped worksheet with the pull results. The original data will stay intact. Good for keeping track of historical changes.' },
  ],
};

// To derive from this class you need to implement following methods
// in your class:
// 1. @computed get _generatedName_() => will be used as placeholder for flowstep name
// 2. @computed get _isValid_() => used for disabling run / save buttons
// 3. @computed get _buttons_() => 
// 2. _getRenderProps_() => (optional) return all the props you needed in rendering + buttonsProps
// 3. _renderBody_(renderProps) => render all you Components
// 4. _renderCustomOptions_(renderProps) {
//   return (
//     <>
//       { super._renderCustomOptions_(renderProps) } // PullFlowstep::_renderCustomOptions_
//       // your custom components goes here
//     </>
//   );
// } => (optional) render extra options
// 5. _componentDidMount_() => custom code when component is mounted (fetch data)
// 6. 
// 7. async loadFlowstep(flowstep, isExact) {
//   super.loadFlowstep(flowstep); // PullFlowstep::loadFlowstep
//   // your custom options / data here
// }
// 8. async makeFlowstep(makeCopy) {
//  const f = super.makeFlowstep(); // PullFlowstep::makeFlowstep
//  // your custom code filling options / data is here
//  return f;
// }

class PullFlowstep extends Flowstep {
  constructor(reactProps, type) {
    super(reactProps, { type });

    this.options = {
      ...this.options,
      createDataTable: true,
      target: 'A1',
      createNewSheet: true,
      useDropdownsForPicklists: true,
      pullMode: PullMode.Overwrite,
      createDateTimeColumn: true,
    };

    this.areValid = { ...this.areValid, target: true };
  }

  get showCreateDropdownsOption() {
    return true;
  }

  get _buttons_() {
    const allow = !this._readOnly_;
    return { allowSaveAndDelete: allow, allowSaveCopy: false, allowEditExisting: allow, data: this._buttonsData_ };
  };

  get _readOnly_() {
    const { soql, report, csv } = this.license.flowsteps.pull.createOrRemove;
    return [
      { feature: soql, type: Type.Soql },
      { feature: report, type: Type.Report },
      { feature: csv, type: Type.Csv },
    ].some(x => !x.feature.can && this.type === x.type);
  }


  _renderCustomOptions_(renderProps) {
    return (
      <>
        <CellPicker
          label={'Table start cell'}
          tooltip={'The address of the left top cell of the range where the data will be placed'}
          disabled={this._readOnly_}
          value={this.options.target}
          onChange={(value, isValid) => this.handleOptionsAndIsValidChange('target', value, isValid)}
        />

        <CheckboxWithTooltip
          label={'Create data table'}
          tooltip={'Creates an Excel Data Table around the pulled data, which enhances the visual representation of the data and allows easier use of data with multiple excel functions'}
          disabled={this._readOnly_}
          checked={this.options.createDataTable}
          onChange={(_, checked) => this.handleOptionsChange('createDataTable', checked)}
        />

        {this.showCreateDropdownsOption &&
          <CheckboxWithTooltip
            label={'Create drop-downs in picklist columns'}
            tooltip={`Check this box if you'd like us to create Excel Data Validation controls (drop down boxes) in each cell of the columns pulled from picklists and populate them with picklist values`}
            disabled={this._readOnly_}
            checked={this.options.useDropdownsForPicklists}
            onChange={(_, checked) => this.handleOptionsChange('useDropdownsForPicklists', checked)}
          />
        }
      </>
    );
  }

  async _loadFlowstepOnMount_(flowstep) {
    await this._loadFlowstep_(flowstep, true); // always load similar flowsteps as exact
  }

  loadFlowstep(flowstep, isExact) {
    super.loadFlowstep(flowstep, isExact);

    const { createDataTable, useDropdownsForPicklists, pullMode, createDateTimeColumn } = flowstep.options;
    this.options = {
      ...this.options,
      target: flowstep.target,
      createDataTable,
      createNewSheet: false,
      useDropdownsForPicklists,
      pullMode,
      createDateTimeColumn,
    };
  }

  async makeFlowstep(worksheetName) {
    const { useDropdownsForPicklists, createDataTable, pullMode, createDateTimeColumn, createNewSheet } = this.options;

    const makeCopy = this.flowstep?.worksheetId && createNewSheet;
    const flowstep = super.makeFlowstep(makeCopy);

    Object.assign(flowstep, {
      worksheetId: (this.flowstep && !createNewSheet)
        ? this.flowstep.worksheetId
        : (await (createNewSheet
          ? office.createWorksheet(worksheetName)
          : office.getActiveWorksheet())).id,
      target: this.options.target,
    });
    if (createNewSheet &&
      flowstep.options.lastUpdateTimestampCellAddress?.address &&
      flowstep.options.lastUpdateTimestampCellAddress.worksheetId === NEW_WORKSHEET_ID) {
      flowstep.options.lastUpdateTimestampCellAddress.worksheetId = flowstep.worksheetId;
    }

    Object.assign(flowstep.options, { useDropdownsForPicklists, createDataTable, pullMode, createDateTimeColumn });

    return flowstep;
  }

  renderPullModeCombobox(renderProps) {
    const { getStepTitle } = renderProps;
    return (
      <Step title={getStepTitle('Select pull mode')}>
        <SearchableComboBox
          placeholder={`Pull mode options`}
          selectedKey={this.options.pullMode}
          onChange={(event, item) => this.handleOptionsChange('pullMode', item.key)}
          options={Const.PullModeOptions}
          disabled={this._readOnly_}
        />
        {this.options.pullMode === PullMode.Append &&
          <CheckboxWithTooltip
            label={'Add Date/Time column'}
            tooltip={'Create a column with the date and time of execution for each run.'}
            disabled={this._readOnly_}
            checked={this.options.createDateTimeColumn}
            onChange={(_, checked) => this.handleOptionsChange('createDateTimeColumn', checked)}
          />}
      </Step>
    );
  }

  renderCreateNewSheet(renderProps) {
    return (
      <CheckboxWithTooltip
        label={'Create new worksheet'}
        useNativeLabel={true}
        tooltip={`We'll create a new worksheet for you and pull the data there`}
        disabled={this._readOnly_}
        checked={this.options.createNewSheet}
        onChange={(_, checked) => this.handleOptionsChange('createNewSheet', checked)} />
    );
  }
}


export default PullFlowstep;